import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { Checkbox } from '../ui/checkbox'

/** The letting cycle's automatic sends (spec 2026-10-25-12), with their defaults. */
const LETTING_SETTINGS = [
    { code: 'letting.lead_acknowledgement', fallback: true, testId: 'settings-letting-lead-ack-toggle', key: 'lead_ack' },
    { code: 'letting.viewing_reconfirmation', fallback: false, testId: 'settings-letting-reconfirm-toggle', key: 'reconfirm' },
    { code: 'letting.viewing_follow_up', fallback: false, testId: 'settings-letting-follow-up-toggle', key: 'follow_up' },
] as const

/**
 * The "Letting" card of the organization settings: a `kind: setting` has no
 * generic screen, so the three toggles ship here.
 */
export function LettingSettingsCard({ org, canWrite }: { org: string; canWrite: boolean }) {
    const { t } = useTranslation('settings')
    const queryClient = useQueryClient()
    const codes = LETTING_SETTINGS.map((s) => s.code).join(',')
    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/settings', {
        params: { path: { orgCode: org }, query: { codes, kind: 'setting' } },
    })
    const save = $api.useMutation('post', '/api/v1/organizations/{orgCode}/settings/{code}', {
        onSuccess: () =>
            queryClient.invalidateQueries({
                predicate: (q) => {
                    const [method, path] = q.queryKey as [string, string]
                    return method === 'get' && path === '/api/v1/organizations/{orgCode}/settings'
                },
            }),
    })
    const entries = (data as { data?: { code: string; value?: unknown }[] } | undefined)?.data ?? []
    const valueOf = (code: string, fallback: boolean) => {
        const e = entries.find((x) => x.code === code)
        return typeof e?.value === 'boolean' ? e.value : fallback
    }

    return (
        <section className="bg-card rounded-lg border border-border p-6" data-testid="settings-letting-card">
            <h2 className="text-base font-semibold text-foreground">{t('organization.letting.title')}</h2>
            <p className="text-muted-foreground text-sm mt-1 mb-4">{t('organization.letting.description')}</p>
            <div className="space-y-3">
                {LETTING_SETTINGS.map((s) => (
                    <label key={s.code} className="flex items-start gap-3 text-sm">
                        <Checkbox
                            checked={valueOf(s.code, s.fallback)}
                            disabled={!canWrite || save.isPending}
                            onCheckedChange={(v) => save.mutate({ params: { path: { orgCode: org, code: s.code } }, body: { value: v === true } })}
                            data-testid={s.testId}
                            data-checked={valueOf(s.code, s.fallback) ? 'true' : 'false'}
                        />
                        <span>
                            <span className="font-medium">{t(`organization.letting.${s.key}.label`)}</span>
                            <span className="block text-muted-foreground">{t(`organization.letting.${s.key}.help`)}</span>
                        </span>
                    </label>
                ))}
            </div>
        </section>
    )
}
